<template>
  <TransitionRoot as="template" :show="!!show">
    <Dialog as="div" class="fixed inset-0 overflow-hidden z-50" @close="emitClose">
      <div class="absolute inset-0 overflow-hidden">
        <DialogOverlay class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" />

        <div class="pointer-events-none fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="ease-out duration-200"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="ease-in duration-150"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <div
              class="pointer-events-auto relative flex w-full max-w-2xl flex-col overflow-hidden border border-slate-700 bg-slate-800 shadow-2xl"
              style="border-radius: 28px; max-height: 90vh;"
            >
              <!-- Header -->
              <header class="shrink-0 border-b border-slate-700 px-5 py-5">
                <div class="flex items-center justify-between gap-3">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <MapPinIcon class="h-5 w-5 text-indigo-300" />
                      <DialogTitle class="text-lg font-semibold text-white tracking-tight">
                        Podgląd adresu na mapie
                      </DialogTitle>
                    </div>
                    <p v-if="displayAddress" class="mt-1 text-xs text-slate-400 truncate">
                      {{ displayAddress }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="iconBtn"
                    @click="emitClose"
                    v-tooltip="'Zamknij'"
                  >
                    <XMarkIcon class="h-5 w-5 text-slate-300" />
                  </button>
                </div>
              </header>

              <!-- Body -->
              <div class="min-h-0 flex-1 overflow-y-auto p-5">
                <div v-if="loading" class="flex items-center justify-center py-12 text-slate-300">
                  <svg class="animate-spin h-5 w-5 mr-2 text-slate-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  <span class="text-sm">Ładowanie mapy…</span>
                </div>

                <div v-else-if="error" class="rounded-2xl border border-red-500 border-opacity-40 bg-red-900 bg-opacity-20 p-4 text-sm text-red-200">
                  <p class="font-semibold">Nie udało się załadować mapy</p>
                  <p class="mt-1 text-xs opacity-90">{{ error }}</p>
                </div>

                <div v-else-if="mapEmbedUrl" class="space-y-3">
                  <div v-if="coordinates" class="softBox">
                    <div class="grid grid-cols-2 gap-3">
                      <div>
                        <div class="softBoxLabel">Szerokość geograficzna</div>
                        <div class="softBoxValue">{{ coordinates.latitude }}</div>
                      </div>
                      <div>
                        <div class="softBoxLabel">Długość geograficzna</div>
                        <div class="softBoxValue">{{ coordinates.longitude }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="overflow-hidden" style="border-radius: 18px; border: 1px solid rgba(71,85,105,0.6);">
                    <iframe
                      :src="mapEmbedUrl"
                      width="100%"
                      height="420"
                      style="border: 0; display: block;"
                      loading="lazy"
                      referrerpolicy="no-referrer-when-downgrade"
                      allowfullscreen
                    ></iframe>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <footer class="shrink-0 border-t border-slate-700 px-5 py-4">
                <div class="flex items-center justify-end">
                  <button
                    type="button"
                    class="inline-flex items-center gap-2 rounded-2xl border border-slate-600 bg-slate-700 px-4 py-2.5 text-xs font-semibold text-slate-100 shadow-sm transition hover:bg-slate-600"
                    @click="emitClose"
                  >
                    Zamknij
                  </button>
                </div>
              </footer>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import axios from 'axios'
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { MapPinIcon, XMarkIcon } from '@heroicons/vue/24/outline'

export default {
  name: 'ModalMapPreview',
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    MapPinIcon,
    XMarkIcon
  },
  props: {
    show: { type: Boolean, default: false },
    // deliveryAddress: { address, city, postCode, regionName, coordinates: { latitude, longitude } }
    deliveryAddress: { type: Object, default: null }
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      mapEmbedUrl: null,
      error: null
    }
  },
  computed: {
    coordinates() {
      const c = this.deliveryAddress?.coordinates
      if (c && typeof c.latitude === 'number' && typeof c.longitude === 'number') {
        return { latitude: c.latitude, longitude: c.longitude }
      }
      return null
    },
    displayAddress() {
      const d = this.deliveryAddress || {}
      const parts = []
      if (d.address) parts.push(d.address)
      const cityPart = [d.postCode, d.city].filter(Boolean).join(' ')
      if (cityPart) parts.push(cityPart)
      return parts.join(', ')
    }
  },
  watch: {
    show(nv) {
      if (nv) this.loadMap()
      else {
        this.mapEmbedUrl = null
        this.error = null
      }
    },
    deliveryAddress() {
      if (this.show) this.loadMap()
    }
  },
  methods: {
    async loadMap() {
      this.error = null
      this.mapEmbedUrl = null
      const d = this.deliveryAddress || {}
      const c = this.coordinates
      if (!c && !(d.address || d.city)) {
        this.error = 'Brak koordynatów ani adresu do wyświetlenia.'
        return
      }
      this.loading = true
      try {
        const payload = c
          ? { latitude: c.latitude, longitude: c.longitude }
          : { address: d.address || '', city: d.city || '', postCode: d.postCode || '' }
        const res = await axios.post(`${this.$store.state.apiLink}/buildMapEmbedUrl`, payload)
        const body = res.data || {}
        if (!body.success || !body.data?.mapEmbedUrl) {
          this.error = body.log || 'Nie udało się zbudować URL mapy.'
          return
        }
        this.mapEmbedUrl = body.data.mapEmbedUrl
      } catch (e) {
        console.error('Map preview error:', e)
        this.error = 'Błąd połączenia z serwerem.'
      } finally {
        this.loading = false
      }
    },
    emitClose() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.backdrop-soft {
  backdrop-filter: blur(3px);
}

.iconBtn {
  height: 40px;
  width: 40px;
  border-radius: 16px;
  border: 1px solid rgba(100, 116, 139, 0.65);
  background: rgba(51, 65, 85, 0.75);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s ease, border-color 0.18s ease;
}

.iconBtn:hover {
  background: rgba(71, 85, 105, 0.95);
  border-color: rgba(148, 163, 184, 0.75);
}

.softBox {
  border-radius: 18px;
  border: 1px solid rgba(100, 116, 139, 0.45);
  background: rgba(15, 23, 42, 0.4);
  padding: 12px 14px;
}

.softBoxLabel {
  font-size: 11px;
  font-weight: 600;
  color: rgba(148, 163, 184, 1);
}

.softBoxValue {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(226, 232, 240, 1);
  font-variant-numeric: tabular-nums;
}
</style>
